<script lang="ts" setup>
import { ref } from 'vue';
import type { GroupOutput } from '@ilteducation/customer-mgmt-types';
import MembershipChip from '../UserSidePanel/MembershipChip.vue';
import GroupAssignTrigger from './GroupAssignTrigger.vue';
import GroupAssignMenu from './GroupAssignMenu.vue';
import CreateGroupDialog from '../NewGroup/CreateGroupDialog.vue';
import { Rule } from '../../rules';

type Props = {
  organizationUid: string;
  disabled?: boolean;
  label?: string;
  isHeader?: boolean;
  headerTooltip?: string;
  rules?: Rule[];
};

withDefaults(defineProps<Props>(), {
  disabled: false,
});

const modelValue = defineModel<string | undefined>();
const modelName = defineModel<string | undefined>('modelName');

const emit = defineEmits<{
  (e: 'groupSelected', value: { uid: string | undefined; name: string | undefined }): void;
}>();

const menuOpen = ref(false);

const iosDummyInput = ref<HTMLInputElement | null>(null);

const handleFocusRelay = () => {
  iosDummyInput.value?.focus();
};

const selectGroup = (group: GroupOutput) => {
  modelValue.value = group.uid;
  modelName.value = group.name;
  emit('groupSelected', { uid: group.uid, name: group.name });
  menuOpen.value = false;
};

const clearSelection = () => {
  modelValue.value = undefined;
  modelName.value = undefined;
  emit('groupSelected', { uid: undefined, name: undefined });
  menuOpen.value = false;
};

const createDialogOpen = ref(false);
const createDialogInitialName = ref('');

const onCreateClick = (searchTerm: string) => {
  createDialogInitialName.value = searchTerm;
  menuOpen.value = false;
  createDialogOpen.value = true;
};

const onGroupCreated = ({ uid, name }: { uid: string; name: string }) => {
  modelValue.value = uid;
  modelName.value = name;
  emit('groupSelected', { uid, name });
};
</script>

<template>
  <v-validation :rules="rules" :validation-value="modelValue" v-slot="{ errorMessages }">
    <input ref="iosDummyInput" type="text" class="ios-dummy-input" tabindex="-1" aria-hidden="true" />

    <v-menu v-model="menuOpen" :close-on-content-click="false" min-width="280" max-width="360">
      <template #activator="{ props }">
        <slot
          name="activator"
          :props="{
            selectedName: modelName,
            onClick: handleFocusRelay,
            activatorProps: props,
            onClear: clearSelection,
          }"
        >
          <div style="display: contents">
            <MembershipChip
              v-if="!isHeader && modelValue && modelName"
              v-bind="props"
              :group-name="modelName"
              :is-deletable-or-error-message="false"
              :disabled="disabled"
              @delete="clearSelection"
              @click="handleFocusRelay"
            />
            <GroupAssignTrigger
              v-else
              v-bind="props"
              :disabled="disabled"
              :label="label"
              :is-header="isHeader"
              :header-tooltip="headerTooltip"
              @click="handleFocusRelay"
            />
          </div>
        </slot>
      </template>

      <GroupAssignMenu
        :organization-uid="organizationUid"
        :selected-uid="modelValue"
        :open="menuOpen"
        @select="selectGroup"
        @create-click="onCreateClick"
      />
    </v-menu>

    <div v-if="errorMessages.value.length > 0" role="alert" class="text-error v-messages opacity-100 mx-4">
      {{ errorMessages.value.join(',') }}
    </div>

    <CreateGroupDialog
      v-model="createDialogOpen"
      :organization-uid="organizationUid"
      :initial-name="createDialogInitialName"
      @group-created="onGroupCreated"
    />
  </v-validation>
</template>

<style scoped>
/* 3. Hide the input visually, but keep it focusable for Safari */
.ios-dummy-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  z-index: -1;
  pointer-events: none;
}
</style>
